// src/pages/finance-portal/reports/KpiDashboards.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid, Card, CardContent,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControl, InputLabel, Select, MenuItem, CircularProgress, Stack, Chip,
    Divider,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, formatNumber, formatPercent,
    firstOfYearIso, todayIso,
} from './reportShared';

function WidgetCard({ widget, start, end, onDelete }) {
    const [value, setValue] = useState(null);
    const [loading, setLoading] = useState(false);

    const compute = useCallback(async () => {
        if (!widget.kpi_key) return;
        setLoading(true);
        try {
            const res = await axiosInstance.post('/api/finance/kpis/compute', {
                key: widget.kpi_key, start_date: start, end_date: end,
            });
            if (res.data.success) setValue(res.data.value);
        } finally { setLoading(false); }
    }, [widget.kpi_key, start, end]);

    useEffect(() => { compute(); }, [compute]);

    const formatValue = (v) => {
        if (v === null || v === undefined) return '—';
        const u = (widget.config?.unit || '').toLowerCase();
        if (u === 'currency') return formatCurrency(v);
        if (u === 'percent') return formatPercent(v);
        if (u === 'days' || u === 'months') return `${formatNumber(v, 1)} ${u}`;
        return formatNumber(v, 2);
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="caption" color="text.secondary">{widget.kpi_key || 'custom'}</Typography>
                    <Stack direction="row">
                        <IconButton size="small" onClick={compute}><RefreshIcon fontSize="inherit" /></IconButton>
                        {onDelete && (
                            <IconButton size="small" onClick={() => onDelete(widget)}>
                                <DeleteIcon fontSize="inherit" />
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{widget.title}</Typography>
                {loading
                    ? <CircularProgress size={22} />
                    : <Typography variant="h3" sx={{ fontWeight: 700, color: ACCENT, mt: 1 }}>
                        {formatValue(value)}
                    </Typography>
                }
            </CardContent>
        </Card>
    );
}

export default function KpiDashboards() {
    const [dashboards, setDashboards] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [dashboard, setDashboard] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [start, setStart] = useState(firstOfYearIso());
    const [end, setEnd] = useState(todayIso());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Create dashboard dialog
    const [dashDialog, setDashDialog] = useState({ open: false });
    const [dashName, setDashName] = useState('');

    // Add widget dialog
    const [widgetDialog, setWidgetDialog] = useState({ open: false });
    const [widgetForm, setWidgetForm] = useState({
        widget_type: 'number', title: '', kpi_key: '',
    });

    const fetchDashboards = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/kpi-dashboards');
            if (res.data.success) {
                setDashboards(res.data.data || []);
                if (!selectedId && res.data.data?.length) setSelectedId(res.data.data[0].id);
            }
        } catch (e) { /* ignore */ }
    }, [selectedId]);

    const fetchDashboard = useCallback(async () => {
        if (!selectedId) { setDashboard(null); return; }
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/kpi-dashboards/${selectedId}`);
            if (res.data.success) setDashboard(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load dashboard');
        } finally { setLoading(false); }
    }, [selectedId]);

    useEffect(() => { fetchDashboards(); }, [fetchDashboards]);
    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    useEffect(() => {
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/kpis/catalog');
                if (res.data.success) setCatalog(res.data.data || []);
            } catch (e) { /* ignore */ }
        })();
    }, []);

    const createDashboard = async () => {
        try {
            const res = await axiosInstance.post('/api/finance/kpi-dashboards', { name: dashName });
            if (res.data.success) {
                setSelectedId(res.data.data.id);
                setDashDialog({ open: false });
                setDashName('');
                fetchDashboards();
            }
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to create');
        }
    };

    const addWidget = async () => {
        if (!dashboard) return;
        try {
            const kpiMeta = catalog.find((k) => k.key === widgetForm.kpi_key);
            const newWidgets = [...(dashboard.widgets || []), {
                widget_type: widgetForm.widget_type,
                title: widgetForm.title || kpiMeta?.name || 'Widget',
                kpi_key: widgetForm.kpi_key,
                config: { unit: kpiMeta?.unit || 'number' },
                display_order: (dashboard.widgets || []).length,
            }];
            await axiosInstance.put(`/api/finance/kpi-dashboards/${dashboard.id}`, {
                widgets: newWidgets,
            });
            setWidgetDialog({ open: false });
            setWidgetForm({ widget_type: 'number', title: '', kpi_key: '' });
            fetchDashboard();
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to add widget');
        }
    };

    const deleteWidget = async (w) => {
        if (!dashboard) return;
        const newWidgets = (dashboard.widgets || []).filter((x) => x.id !== w.id);
        try {
            await axiosInstance.put(`/api/finance/kpi-dashboards/${dashboard.id}`, {
                widgets: newWidgets,
            });
            fetchDashboard();
        } catch (e) {
            alert('Failed to delete widget');
        }
    };

    const deleteDashboard = async () => {
        if (!selectedId || !window.confirm('Delete this dashboard?')) return;
        try {
            await axiosInstance.delete(`/api/finance/kpi-dashboards/${selectedId}`);
            setSelectedId('');
            setDashboard(null);
            fetchDashboards();
        } catch (e) {
            alert('Failed to delete');
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <DashboardIcon sx={{ color: ACCENT }} />
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>KPI Dashboards</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Button startIcon={<AddIcon />} onClick={() => setDashDialog({ open: true })}>
                        New Dashboard
                    </Button>
                    {dashboard && (
                        <Button startIcon={<AddIcon />} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}
                            onClick={() => setWidgetDialog({ open: true })}>
                            Add Widget
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Dashboard</InputLabel>
                            <Select label="Dashboard" value={selectedId}
                                onChange={(e) => setSelectedId(e.target.value)}>
                                {dashboards.map((d) => (
                                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField label="Start" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField label="End" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                        {dashboard && (
                            <IconButton onClick={deleteDashboard} color="error">
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {dashboard && !loading && (
                <Grid container spacing={2}>
                    {(dashboard.widgets || []).length === 0 && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                No widgets yet — click "Add Widget" to pick KPIs from the catalog.
                            </Alert>
                        </Grid>
                    )}
                    {(dashboard.widgets || []).map((w) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={w.id}>
                            <WidgetCard widget={w} start={start} end={end}
                                onDelete={() => deleteWidget(w)} />
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={dashDialog.open} onClose={() => setDashDialog({ open: false })}>
                <DialogTitle>New Dashboard</DialogTitle>
                <DialogContent>
                    <TextField label="Name" fullWidth value={dashName}
                        onChange={(e) => setDashName(e.target.value)} sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDashDialog({ open: false })}>Cancel</Button>
                    <Button variant="contained" onClick={createDashboard}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}
                        disabled={!dashName}>Create</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={widgetDialog.open} onClose={() => setWidgetDialog({ open: false })}
                maxWidth="sm" fullWidth>
                <DialogTitle>Add Widget</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>KPI</InputLabel>
                                <Select label="KPI" value={widgetForm.kpi_key}
                                    onChange={(e) => {
                                        const k = catalog.find((x) => x.key === e.target.value);
                                        setWidgetForm({
                                            ...widgetForm,
                                            kpi_key: e.target.value,
                                            title: k?.name || widgetForm.title,
                                        });
                                    }}>
                                    {catalog.map((k) => (
                                        <MenuItem key={k.key} value={k.key}>
                                            {k.category} · {k.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField label="Title" fullWidth size="small"
                                value={widgetForm.title}
                                onChange={(e) => setWidgetForm({ ...widgetForm, title: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select label="Type" value={widgetForm.widget_type}
                                    onChange={(e) => setWidgetForm({ ...widgetForm, widget_type: e.target.value })}>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="gauge">Gauge</MenuItem>
                                    <MenuItem value="line">Line</MenuItem>
                                    <MenuItem value="bar">Bar</MenuItem>
                                    <MenuItem value="table">Table</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWidgetDialog({ open: false })}>Cancel</Button>
                    <Button variant="contained" onClick={addWidget}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}
                        disabled={!widgetForm.kpi_key}>Add</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
